<template>
  <form @submit.prevent="onSubmit">
    <h2>{{ t('station') }}</h2>

    <div class="mt-4 grid grid-cols-3 gap-4">
      <InputField name="info.de" :label="t('nameDe')" class="col-start-1" />
      <InputField name="info.en" :label="t('nameEn')" />
      <InputField name="notice.de" :label="t('noticeDe')" class="col-start-1" />
      <InputField name="notice.en" :label="t('noticeEn')" />
      <SearchLocation v-model:location="location" hide-address class="col-start-1" />
      <InputField name="street" :label="t('street')" class="col-start-1" />
      <div class="flex gap-2">
        <InputField class="basis-2/5" :name="`zip`" :label="t('zip')" />
        <InputField :name="`city`" :label="t('city')" />
      </div>
      <SearchableSelectField
        name="countryCode"
        :label="t('country')"
        :options="countryOptions"
        class="col-start-1"
        :track-by="['label', 'labelEn', 'labelShort', 'value', 'alternativeNames']"
      />
      <SelectField
        name="state"
        :label="t('state')"
        :options="stateOptions"
        empty-value-to="null"
        :placeholder="stateOptions.length ? t('selectHolidayRegion') : t('noHolidayRegion')"
        :disabled="!stateOptions.length"
      />
      <SelectField
        v-if="legalEntityOptions.length > 1"
        name="legalEntityId"
        :label="t('legalEntity')"
        :options="legalEntityOptions"
        class="col-start-1"
      />
      <p v-if="isLegalEntityChanged" class="col-span-full text-sm text-bittersweet-600">
        {{ t('legalEntityChangeHint') }}
      </p>
      <InputField name="slug" :label="t('slug')" />
      <InputField name="invoiceSuffix" :label="t('invoiceSuffix')" />
      <SelectField name="icon" :label="t('icon')" :options="iconOptions" empty-value-to="null" />
      <SelectField
        name="isGoogleMapsReviewEnabled"
        :label="t('isGoogleMapsReviewEnabled')"
        :options="googleMapsReviewEnabledOptions"
        class="col-span-1 col-start-1"
      />
      <InputField
        name="googleMapsReviewCtaUrl"
        :label="t('googleMapsReviewCtaUrl')"
        empty-value-to="null"
        class="col-span-2"
      />
    </div>

    <Divider />

    <div class="flex items-center justify-between">
      <h2>{{ t('videoWall') }}</h2>
      <CVButton
        v-if="station?.id"
        variant="secondary"
        :disabled="isLoading || !$can('update', subject('Station', station), 'authCode')"
        :is-loading="isRegeneratingVideoWallAuthCode"
        @click.prevent="handleRegenerateVideoWallAuthCode"
      >
        {{ t('refreshVideoWallAuthCode') }}
      </CVButton>
    </div>
    <div class="mt-4 space-y-3">
      <p v-if="videoWallUrl" class="flex flex-wrap items-center gap-2 text-sm text-gray-600">
        {{ t('videoWallUrl') }}:
        <a :href="videoWallUrl" class="text-primary underline" target="_blank" rel="noopener">
          {{ videoWallUrl }}
        </a>
        <CopyToClipboardButton :text="videoWallUrl" />
      </p>
      <p v-else-if="!props.station?.slug" class="text-sm text-gray-500">
        {{ t('videoWallUrlNeedsSlug') }}
      </p>
    </div>

    <Divider />

    <h2>{{ t('openingHours') }}</h2>

    <div class="mt-4 divide-y rounded-md bg-white px-4">
      <div v-for="(day, index) in openingDays" :key="index" class="flex items-start gap-4 py-3">
        <div class="flex h-8 w-36 shrink-0 items-center">
          <Checkbox v-model="day.opened">{{ t(`weekdays.${weekdays[index]}`) }}</Checkbox>
        </div>
        <div v-if="day.opened" class="min-w-0 flex-1">
          <OpeningRangesInput v-model="day.ranges" />
        </div>
        <div v-else class="flex h-8 flex-1 items-center text-gray-500">{{ t('closed') }}</div>
        <CVButton
          v-if="copiedDayIndex === index"
          variant="secondary"
          size="sm"
          class="w-36 shrink-0"
          :title="t('stopCopying')"
          @click.prevent="copiedDayIndex = null"
        >
          <CheckIcon class="size-4" />
          {{ t('copied') }}
        </CVButton>
        <CVButton
          v-else-if="copiedDayIndex !== null"
          variant="ghost"
          size="sm"
          class="w-36 shrink-0"
          @click.prevent="handlePasteDay(index)"
        >
          <ClipboardPasteIcon class="size-4" />
          {{ t('paste') }}
        </CVButton>
        <CVButton
          v-else-if="day.opened"
          variant="ghost"
          size="sm"
          class="w-36 shrink-0"
          @click.prevent="copiedDayIndex = index"
        >
          <CopyIcon class="size-4" />
          {{ t('copy') }}
        </CVButton>
        <div v-else class="w-36 shrink-0" />
      </div>
    </div>

    <Divider />

    <IgnoredHolidaysForm :station="station" />

    <Divider />

    <StationCountersForm />

    <Divider />

    <div class="space-y-4">
      <SelectField
        name="returnPolicy"
        :label="t('returnPolicy')"
        :options="returnPolicyOptions"
        class="col-span-3"
      />
      <CheckboxField name="isHidden" :label="t('stationIsHidden')" class="col-span-3" />
      <CheckboxField name="isInternal" :label="t('stationIsInternal')" class="col-span-3" />
    </div>

    <Divider />

    <div class="mt-3 flex justify-between">
      <CVButton
        v-if="station?.id"
        :disabled="isLoading"
        variant="danger"
        @click.prevent="emit('onDelete')"
      >
        {{ t('delete') }}
      </CVButton>
      <div class="flex flex-1 justify-end">
        <CVButton :is-loading="isLoading" @click="onSubmit">{{ t('save') }}</CVButton>
      </div>
    </div>
  </form>
</template>

<script lang="ts" setup>
import Divider from '@/components/Divider.vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { cloneDeep, kebabCase } from 'lodash';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import type { Station, UpdateStationDto } from '@/entities/station/station.entity';
import { stationSchema } from '@/validation/station.schema';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import { type ExtendedGeocodedLocation } from '@/components/GooglePlacesInput.vue';
import SearchLocation from '@/components/booking-forms/SearchLocation.vue';
import OpeningRangesInput from './OpeningRangesInput.vue';
import CheckboxField from '@/components/CheckboxField.vue';
import Checkbox from '@/components/Checkbox.vue';
import { CheckIcon, ClipboardPasteIcon, CopyIcon } from 'lucide-vue-next';
import IgnoredHolidaysForm from './IgnoredHolidaysForm.vue';
import StationCountersForm from './StationCountersForm.vue';
import {
  StationIcon,
  StationReturnPolicy,
  type IgnoredHolidayDto,
  type TimeRange,
} from '@carvia/ros-client-types';
import SearchableSelectField from '@/components/SearchableSelectField.vue';
import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { getCountries } from '@/utils/countries';
import { Alert } from '@/services/alert.service';
import { useUpdateStation, useHolidaySubdivisions } from '@/queries/use-stations';
import { subject } from '@casl/ability';
import { useLegalEntities } from '@/queries/use-legal-entities';

const props = defineProps<{
  station?: Station;
  isLoading: boolean;
  isFetching: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: UpdateStationDto): void;
  (e: 'onDelete'): void;
  (e: 'refetch'): void;
}>();

const { t, locale } = useI18n();

const countryOptions = computed(() => getCountries(locale.value));

const DEFAULT_OPENING_RANGES: TimeRange[] = [{ start: 9 * 60, end: 18 * 60 }];

const openingDays = ref<{ opened: boolean; ranges: TimeRange[] }[]>(
  Array.from({ length: 7 }, (_, index) => {
    if (!props.station) return { opened: true, ranges: cloneDeep(DEFAULT_OPENING_RANGES) };
    const ranges =
      props.station.openingHours.find((hours) => hours.weekday === index + 1)?.ranges ?? [];
    return ranges.length
      ? { opened: true, ranges: cloneDeep(ranges) }
      : { opened: false, ranges: cloneDeep(DEFAULT_OPENING_RANGES) };
  }),
);

/** Weekday whose ranges can be pasted into other days, until copying is stopped. */
const copiedDayIndex = ref<number | null>(null);

const handlePasteDay = (index: number) => {
  if (copiedDayIndex.value === null) return;
  openingDays.value[index] = {
    opened: true,
    ranges: cloneDeep(openingDays.value[copiedDayIndex.value].ranges),
  };
};

const getOpeningHoursFromDays = () =>
  openingDays.value.map((day, index) => ({
    weekday: index + 1,
    ranges: day.opened ? cloneDeep(day.ranges) : [],
  }));

watch(openingDays, () => setFieldValue('openingHours', getOpeningHoursFromDays()), {
  deep: true,
});

const location = ref<ExtendedGeocodedLocation | undefined>(
  props.station?.location
    ? {
        coordinates: {
          latitude: props.station.location.coordinates[1],
          longitude: props.station.location.coordinates[0],
        },
      }
    : undefined,
);

const initialValues = {
  info: props.station?.info,
  slug: props.station?.slug,
  location: props.station?.location
    ? {
        longitude: props.station.location.coordinates[0],
        latitude: props.station.location.coordinates[1],
      }
    : undefined,
  street: props.station?.street,
  zip: props.station?.zip,
  city: props.station?.city,
  state: props.station?.state,
  countryCode: props.station?.countryCode ?? 'DE',
  icon: props.station?.icon ?? null,
  invoiceSuffix: props.station?.invoiceSuffix,
  googleMapsReviewCtaUrl: props.station?.googleMapsReviewCtaUrl ?? null,
  isGoogleMapsReviewEnabled: props.station?.isGoogleMapsReviewEnabled ? 'true' : 'false',
  notice: props.station?.notice,
  openingHours: getOpeningHoursFromDays(),
  returnPolicy: props.station?.returnPolicy ?? StationReturnPolicy.AFTER_HOURS_KEYBOX,
  isHidden: props.station?.isHidden ?? false,
  isInternal: props.station?.isInternal ?? false,
  legalEntityId: props.station?.legalEntity?.id,
  ignoredHolidays: props.station?.ignoredHolidays
    ? props.station.ignoredHolidays.map((ignored: IgnoredHolidayDto) => {
        const isSpecificDate = 'specificDate' in ignored.holiday && ignored.holiday.specificDate;
        return {
          ...ignored,
          holidayType: isSpecificDate ? 'specificDate' : 'publicHoliday',
        };
      })
    : [],
  counters:
    props.station?.counters?.map((counter) => ({
      id: counter.id,
      name: counter.name,
    })) ?? [],
};

// Set address fields, when location changes
watch(location, () => {
  if (location.value?.coordinates) {
    setFieldValue('location', location.value.coordinates);
    location.value.address_components?.forEach((component) => {
      if (component.types.includes('postal_code')) {
        setFieldValue('zip', component.long_name);
      } else if (component.types.includes('locality')) {
        setFieldValue('city', component.long_name);
      } else if (component.types.includes('route')) {
        const number = location.value?.address_components?.find((comp) =>
          comp.types.includes('street_number'),
        )?.long_name;
        setFieldValue('street', component.long_name + (number ? ` ${number}` : ''));
      } else if (component.types.includes('administrative_area_level_1')) {
        setFieldValue('state', component.short_name?.toUpperCase());
      } else if (component.types.includes('country')) {
        const code = component.short_name?.toUpperCase();
        if (code?.length === 2) {
          setFieldValue('countryCode', code);
        }
      }
    });
  }
});

const iconOptions = computed(() => [
  { label: t('iconNone'), value: null },
  ...Object.values(StationIcon).map((value) => ({
    label: t(`stationIcons.${value}`),
    value,
  })),
]);

const returnPolicyOptions = computed(() =>
  Object.values(StationReturnPolicy).map((value) => ({
    label: t(`returnPolicies.${value}`),
    value,
  })),
);

const googleMapsReviewEnabledOptions = computed(() => [
  { label: t('active'), value: 'true' },
  { label: t('inactive'), value: 'false' },
]);

const { data: legalEntities } = useLegalEntities();
// Only offered with several legal entities; otherwise the backend uses the only one.
const legalEntityOptions = computed(() =>
  (legalEntities.value ?? [])
    .filter(
      (legalEntity) => legalEntity.isActive || legalEntity.id === props.station?.legalEntity?.id,
    )
    .map((legalEntity) => ({ label: legalEntity.name, value: legalEntity.id })),
);

const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const {
  handleSubmit,
  values: formValues,
  setFieldValue,
} = useForm({
  initialValues,
  validationSchema: stationSchema,
});

const holidayLang = computed(() => locale.value.split('-')[0]);
const { data: holidaySubdivisions, isFetched: holidaySubdivisionsFetched } = useHolidaySubdivisions(
  computed(() => formValues.countryCode),
  holidayLang,
  computed(() => (formValues.countryCode?.length ?? 0) === 2),
);

const stateOptions = computed(() =>
  (holidaySubdivisions.value ?? []).map((subdivision) => ({
    label: subdivision.name,
    value: subdivision.code,
  })),
);

watch(
  () =>
    [formValues.countryCode, holidaySubdivisions.value, holidaySubdivisionsFetched.value] as const,
  () => {
    if (!holidaySubdivisionsFetched.value) return;
    const codes = new Set((holidaySubdivisions.value ?? []).map((subdivision) => subdivision.code));
    if (formValues.state && !codes.has(formValues.state)) {
      setFieldValue('state', null);
    }
  },
);

// Existing bookings keep their legal entity (ADR 0021); only new bookings follow the station.
const isLegalEntityChanged = computed(
  () => !!props.station?.legalEntity && formValues.legalEntityId !== props.station.legalEntity.id,
);

const unchangedValues = ref(cloneDeep(formValues));

// Not every invalid field shows its own message (e.g. opening hours), so always say why nothing was saved
const handleInvalidSubmit = () => {
  Alert.fire({ titleText: t('invalidFormTitle'), text: t('invalidFormText'), icon: 'error' });
};

const onSubmit = handleSubmit((values) => {
  unchangedValues.value = cloneDeep(formValues);
  values.ignoredHolidays?.forEach((ignored: any) => {
    delete ignored.holidayType;
  });
  emit('onSubmit', {
    ...values,
    isGoogleMapsReviewEnabled: values.isGoogleMapsReviewEnabled === 'true',
  });
}, handleInvalidSubmit);

usePreventLeavingUnsavedForm(() => formHasChanged(unchangedValues.value, formValues));

const { mutateAsync: updateStation, isPending: isRegeneratingVideoWallAuthCode } =
  useUpdateStation();

const videoWallUrl = computed(() => {
  const slug = props.station?.slug;
  const code = props.station?.authCode;
  if (!slug || !code) return null;
  return `${window.location.origin}/video-wall/${slug}?code=${code}`;
});

async function handleRegenerateVideoWallAuthCode() {
  if (!props.station?.id) return;

  const { isConfirmed } = await Alert.fire({
    titleText: t('regenerateVideoWallAuthCodeTitle'),
    text: t('regenerateVideoWallAuthCodeText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('refreshVideoWallAuthCode'),
    denyButtonText: t('cancel'),
  });
  if (!isConfirmed) return;

  const authCode = (crypto.getRandomValues(new Uint32Array(1))[0] % 1_000_000)
    .toString()
    .padStart(6, '0');

  await updateStation({
    id: props.station.id,
    station: { authCode },
  });
}

watch(
  () => formValues.info?.de,
  (value) => {
    if (props.station) return;
    setFieldValue('slug', kebabCase(value));
    setFieldValue('invoiceSuffix', value?.match(/([a-zA-Z]){1,2}/)?.[0].toUpperCase());
  },
);
</script>

<i18n lang="json">
{
  "en": {
    "legalEntity": "Legal entity",
    "legalEntityChangeHint": "Only new bookings belong to the new legal entity. Existing bookings, their invoices and payments keep the previous legal entity.",
    "station": "Station",
    "stationIsHidden": "Station is hidden: Will not be displayed on website, but cars can still be booked publicly",
    "stationIsInternal": "Station is internal: Will not be displayed on website & cars of the station can only be seen and booked internally",
    "nameDe": "Name DE",
    "nameEn": "Name EN",
    "noticeDe": "Notice DE",
    "noticeEn": "Notice EN",
    "street": "Street",
    "zip": "Zip",
    "city": "City",
    "country": "Country",
    "state": "Holiday region",
    "selectHolidayRegion": "Select region",
    "noHolidayRegion": "Not applicable",
    "slug": "Slug",
    "invoiceSuffix": "Invoice Suffix",
    "googleMapsReviewCtaUrl": "Google Maps review URL",
    "isGoogleMapsReviewEnabled": "Google Maps review after NPS",
    "active": "Active",
    "inactive": "Inactive",
    "icon": "Icon",
    "iconNone": "No icon",
    "returnPolicy": "Car return",
    "returnPolicies": {
      "AFTER_HOURS_KEYBOX": "Also outside opening hours (key box)",
      "OPENING_HOURS_ONLY": "Only during opening hours (no key box)"
    },
    "stationIcons": {
      "city": "City",
      "airport": "Airport",
      "train": "Train station",
      "palm": "Palm",
      "car": "Car"
    },
    "videoWall": "Video wall",
    "refreshVideoWallAuthCode": "Regenerate link",
    "regenerateVideoWallAuthCodeTitle": "Regenerate video wall link?",
    "regenerateVideoWallAuthCodeText": "The current link will stop working immediately. Displays using the old link will need the new URL.",
    "videoWallUrl": "Video wall URL",
    "videoWallUrlNeedsSlug": "Set a slug to generate the video wall URL.",
    "openingHours": "Opening Hours",
    "closed": "Closed",
    "invalidFormTitle": "Not saved",
    "invalidFormText": "Please check the highlighted fields, e.g. overlapping opening hours.",
    "copy": "Copy",
    "copied": "Copied",
    "paste": "Paste",
    "stopCopying": "Stop pasting",
    "delete": "Delete"
  },
  "de": {
    "legalEntity": "Gesellschaft",
    "legalEntityChangeHint": "Nur neue Buchungen gehören zur neuen Gesellschaft. Bestehende Buchungen mit ihren Rechnungen und Zahlungen bleiben bei der bisherigen Gesellschaft.",
    "station": "Station",
    "stationIsHidden": "Station ist versteckt: Wird auf Webseite nicht angezeigt, aber Autos können trotzdem öffentlich gebucht werden",
    "stationIsInternal": "Station ist intern: Wird auf Webseite nicht angezeigt & Autos der Station können nur intern gesehen und gebucht werden",
    "stationIsActive": "Station ist aktiv (wird auf Webseite angezeigt, etc.)",
    "nameDe": "Name DE",
    "nameEn": "Name EN",
    "noticeDe": "Zusatzinfo DE",
    "noticeEn": "Zusatzinfo EN",
    "street": "Straße",
    "zip": "PLZ",
    "city": "Ort",
    "country": "Land",
    "state": "Feiertagsregion",
    "selectHolidayRegion": "Region wählen",
    "noHolidayRegion": "Nicht erforderlich",
    "slug": "Slug",
    "invoiceSuffix": "Rechnungssuffix",
    "googleMapsReviewCtaUrl": "Google-Maps-Bewertungs-URL",
    "isGoogleMapsReviewEnabled": "Google-Maps-Bewertung nach NPS",
    "active": "Aktiv",
    "inactive": "Inaktiv",
    "icon": "Icon",
    "iconNone": "Kein Icon",
    "returnPolicy": "Fahrzeugrückgabe",
    "returnPolicies": {
      "AFTER_HOURS_KEYBOX": "Auch außerhalb der Öffnungszeiten (Keybox)",
      "OPENING_HOURS_ONLY": "Nur während der Öffnungszeiten (keine Keybox)"
    },
    "stationIcons": {
      "city": "Stadt",
      "airport": "Flughafen",
      "train": "Bahnhof",
      "palm": "Palme",
      "car": "Auto"
    },
    "videoWall": "Video Wall",
    "refreshVideoWallAuthCode": "Link neu erzeugen",
    "regenerateVideoWallAuthCodeTitle": "Video-Wall-Link neu erzeugen?",
    "regenerateVideoWallAuthCodeText": "Der aktuelle Link funktioniert danach nicht mehr. Displays mit dem alten Link benötigen die neue URL.",
    "videoWallUrl": "Video-Wall-URL",
    "videoWallUrlNeedsSlug": "Lege einen Slug fest, um die Video-Wall-URL zu erzeugen.",
    "openingHours": "Öffnungszeiten",
    "closed": "Geschlossen",
    "invalidFormTitle": "Nicht gespeichert",
    "invalidFormText": "Bitte prüfe die markierten Felder, z. B. sich überschneidende Öffnungszeiten.",
    "copy": "Kopieren",
    "copied": "Kopiert",
    "paste": "Einfügen",
    "stopCopying": "Einfügen beenden",
    "delete": "Löschen"
  },
  "es": {
    "legalEntity": "Sociedad",
    "legalEntityChangeHint": "Solo las reservas nuevas pertenecen a la nueva sociedad. Las reservas existentes, con sus facturas y pagos, se quedan en la sociedad anterior.",
    "station": "Estación",
    "stationIsHidden": "Estación oculta: no se muestra en la web, pero los coches siguen pudiendo reservarse públicamente",
    "stationIsInternal": "Estación interna: no se muestra en la web y los coches solo se ven y reservan internamente",
    "nameDe": "Nombre DE",
    "nameEn": "Nombre EN",
    "noticeDe": "Aviso DE",
    "noticeEn": "Aviso EN",
    "street": "Calle",
    "zip": "Código postal",
    "city": "Ciudad",
    "country": "País",
    "state": "Región festiva",
    "selectHolidayRegion": "Seleccionar región",
    "noHolidayRegion": "No aplicable",
    "slug": "Slug",
    "invoiceSuffix": "Sufijo de factura",
    "googleMapsReviewCtaUrl": "URL de reseña de Google Maps",
    "isGoogleMapsReviewEnabled": "Reseña de Google Maps tras NPS",
    "active": "Activo",
    "inactive": "Inactivo",
    "icon": "Icono",
    "iconNone": "Sin icono",
    "returnPolicy": "Devolución del coche",
    "returnPolicies": {
      "AFTER_HOURS_KEYBOX": "También fuera del horario (buzón de llaves)",
      "OPENING_HOURS_ONLY": "Solo en horario de apertura (sin buzón de llaves)"
    },
    "stationIcons": {
      "city": "Ciudad",
      "airport": "Aeropuerto",
      "train": "Estación de tren",
      "palm": "Palmera",
      "car": "Coche"
    },
    "videoWall": "Video wall",
    "refreshVideoWallAuthCode": "Regenerar enlace",
    "regenerateVideoWallAuthCodeTitle": "¿Regenerar enlace del video wall?",
    "regenerateVideoWallAuthCodeText": "El enlace actual dejará de funcionar de inmediato. Las pantallas con el enlace antiguo necesitarán la nueva URL.",
    "videoWallUrl": "URL del video wall",
    "videoWallUrlNeedsSlug": "Define un slug para generar la URL del video wall.",
    "openingHours": "Horario",
    "closed": "Cerrado",
    "invalidFormTitle": "No guardado",
    "invalidFormText": "Revisa los campos marcados, p. ej. horarios que se solapan.",
    "copy": "Copiar",
    "copied": "Copiado",
    "paste": "Pegar",
    "stopCopying": "Terminar de pegar",
    "delete": "Eliminar"
  }
}
</i18n>
